import { useEffect, useMemo, useRef, useState } from "react";
import { FaMapMarkedAlt, FaCheckCircle, FaTimesCircle, FaChevronRight, FaChevronLeft, FaDownload, FaExternalLinkAlt, FaCopy } from "react-icons/fa";
import Swal from "sweetalert2";
import { useAuth } from "../context/AuthContext";
import WindowLayout from "../utils/layout/windowsLayout";
import styles from "./rutas.module.css";
import Select from 'react-select';
import { api } from "../utils/services/api";
import { SERVICE } from "../utils/constants/services";
import * as XLSX from 'xlsx';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Función para calcular distancia entre dos puntos (Haversine)
const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Radio de la Tierra en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// Función para optimizar la ruta (algoritmo voraz: siempre ir al más cercano)
const optimizeRoute = (startLocation, clients) => {
    if (!clients || clients.length === 0) return [];
    
    const route = [];
    const visited = new Set();
    let currentLocation = startLocation;
    let totalDistance = 0;
    
    while (visited.size < clients.length) {
        let nearestIdx = -1;
        let minDistance = Infinity;
        
        for (let i = 0; i < clients.length; i++) {
            if (!visited.has(i)) {
                const distance = calculateDistance(
                    currentLocation.lat,
                    currentLocation.lng,
                    clients[i].latitude,
                    clients[i].longitude
                );
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestIdx = i;
                }
            }
        }
        
        if (nearestIdx !== -1) {
            visited.add(nearestIdx);
            totalDistance += minDistance;
            route.push({ 
                ...clients[nearestIdx], 
                originalIndex: nearestIdx, 
                distance: minDistance,
                cumulativeDistance: totalDistance 
            });
            currentLocation = { lat: clients[nearestIdx].latitude, lng: clients[nearestIdx].longitude };
        }
    }
    
    return route;
};

// Función para generar URLs de Google Maps (máximo 10 waypoints por URL)
const generateGoogleMapsURLs = (startLocation, route) => {
    const urls = [];
    const maxWaypoints = 9; // 1 origen + 9 waypoints + 1 destino = 10 puntos máximo
    
    let currentStart = startLocation;
    
    for (let i = 0; i < route.length; i += maxWaypoints) {
        const batch = route.slice(i, i + maxWaypoints);
        const origin = `${currentStart.lat},${currentStart.lng}`;
        const destination = batch.length > 0 ? `${batch[batch.length - 1].latitude},${batch[batch.length - 1].longitude}` : origin;
        
        let waypoints = '';
        if (batch.length > 1) {
            waypoints = batch.slice(0, -1)
                .map(client => `${client.latitude},${client.longitude}`)
                .join('|');
        }
        
        const params = new URLSearchParams({
            api: '1',
            origin: origin,
            destination: destination,
        });
        
        if (waypoints) {
            params.append('waypoints', waypoints);
        }
        
        const url = `https://www.google.com/maps/dir/?${params.toString()}`;
        urls.push({
            url: url,
            clients: batch,
            startPoint: currentStart,
            endPoint: { lat: batch[batch.length - 1]?.latitude, lng: batch[batch.length - 1]?.longitude }
        });
        
        currentStart = { lat: batch[batch.length - 1]?.latitude, lng: batch[batch.length - 1]?.longitude };
    }
    
    return urls;
};

// Función para exportar a Excel
const exportToExcel = (route, userLocation) => {
    if (!route || route.length === 0) {
        Swal.fire({
            icon: "warning",
            title: "Sin datos",
            text: "No hay clientes para exportar",
        });
        return;
    }

    const urlsData = generateGoogleMapsURLs(userLocation, route);
    const excelData = [];
    
    urlsData.forEach((urlObj, batchIdx) => {
        urlObj.clients.forEach((client, clientIdx) => {
            excelData.push({
                'Ruta #': batchIdx + 1,
                'Orden en Ruta': route.indexOf(client) + 1,
                'Nombre': client.clientName,
                'Dirección': client.clientDireccion,
                'Latitud': client.latitude,
                'Longitud': client.longitude,
                'Distancia (km)': client.distance.toFixed(2),
                'URL Google Maps': urlObj.url,
            });
        });
    });
    
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rutas");
    
    // Ajustar ancho de columnas
    worksheet['!cols'] = [
        { wch: 10 },
        { wch: 12 },
        { wch: 20 },
        { wch: 40 },
        { wch: 12 },
        { wch: 12 },
        { wch: 14 },
        { wch: 80 }
    ];
    
    XLSX.writeFile(workbook, `rutas_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    Swal.fire({
        icon: "success",
        title: "Exportado",
        text: `Archivo exportado con ${excelData.length} clientes en ${urlsData.length} ruta(s)`,
    });
};

export default function Rutas() {
    const { setMenuActual, userId } = useAuth();
    const [asesores, setAsesores] = useState([]);
    const [asesorLocked, setAsesorLocked] = useState(false);
    const [asesorSelect, setAsesorSelect] = useState(null);
    const [clients, setClients] = useState(null);
    const [optimizedRoute, setOptimizedRoute] = useState([]);
    const [visitedClients, setVisitedClients] = useState(new Set());
    const [viewMode, setViewMode] = useState('all'); // 'all', 'one', 'three'
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showURLPreview, setShowURLPreview] = useState(false);
    const [urlsPreview, setUrlsPreview] = useState([]);

    const [loadingPrint, setLoadingPrint] = useState(false);
    const [fecha, setFecha] = useState(new Date().toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: 'America/Mexico_City'
    }).split('/').reverse().join('-'));

    const mapContainerRef = useRef(null);
    const [map, setMap] = useState(null);
    const [userLocation, setUserLocation] = useState(null);
    const userMarkerRef = useRef(null);
    const clientMarkersRef = useRef([]);
    const polylineRef = useRef(null);

    const handleGetAsesortes = () => {
        api(SERVICE.getAsesoresAll)
            .then((data) => {
                let arrayTemp = [];
                if (data.data.data) {
                    for (let index = 0; index < data.data.data.length; index++) {
                        const element = data.data.data[index];
                        arrayTemp.push({
                            value: element.id,
                            label: element.completeName,
                        });
                    }
                }
                setAsesores(arrayTemp);
                handleGetInfoUser(arrayTemp);
            })
            .catch((err) => {
                console.log(err);
            });
    };
    const handleGetInfoUser = (asesoresTemp) => {
        api(SERVICE.getUserById + userId)
            .then((data) => {
                if (data.data.data) {
                    let dataUser = data.data.data;
                    if (dataUser.advisorId === null || dataUser.advisorId === 0) {
                        setAsesorLocked(false);
                    } else {
                        let findIndex = asesoresTemp.find(
                            (a) => a.value === dataUser.advisorId,
                        );
                        if (findIndex) {
                            setAsesorSelect(findIndex);
                            setAsesorLocked(true);
                        }
                    }
                }
            })
            .catch((err) => {
                console.log(err);
            });
    };
    useEffect(() => {
        // Cargar script de Google Maps
        if (!window.google) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
            script.async = true;
            script.onload = () => {
                if (mapContainerRef.current && !map) {
                    initMap();
                }
            };
            document.head.appendChild(script);
        } else if (mapContainerRef.current && !map) {
            initMap();
        }
    }, [map]);

    const initMap = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const location = { lat: latitude, lng: longitude };
                    setUserLocation(location);

                    const newMap = new window.google.maps.Map(mapContainerRef.current, {
                        center: location,
                        zoom: 14,
                    });
                    setMap(newMap);

                    // Crear marcador más visible para la ubicación actual
                    userMarkerRef.current = new window.google.maps.Marker({
                        position: location,
                        map: newMap,
                        title: "Mi ubicación actual",
                        icon: {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 12,
                            fillColor: '#4285F4',
                            fillOpacity: 1,
                            strokeWeight: 3,
                            strokeColor: '#FFFFFF',
                        },
                        zIndex: 1000,
                    });

                    // Pulso animado para el marcador actual
                    const pulse = setInterval(() => {
                        if (userMarkerRef.current) {
                            const currentScale = userMarkerRef.current.getIcon().scale;
                            userMarkerRef.current.setIcon({
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: currentScale === 12 ? 14 : 12,
                                fillColor: '#4285F4',
                                fillOpacity: 1,
                                strokeWeight: 3,
                                strokeColor: '#FFFFFF',
                            });
                        }
                    }, 500);

                    // Observar cambios en la ubicación en tiempo real
                    navigator.geolocation.watchPosition((newPosition) => {
                        const newLocation = { lat: newPosition.coords.latitude, lng: newPosition.coords.longitude };
                        setUserLocation(newLocation);
                        if (userMarkerRef.current) {
                            userMarkerRef.current.setPosition(newLocation);
                        }
                    }, (error) => {
                        console.error("Error obteniendo la ubicación en tiempo real:", error);
                    }, { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 });

                    return () => clearInterval(pulse);
                },
                (error) => {
                    console.error("Error obteniendo la ubicación:", error);
                    console.error("Código de error:", error.code);
                    const defaultLocation = { lat: 18.647336, lng: -91.820681 };
                    setUserLocation(defaultLocation);
                    const newMap = new window.google.maps.Map(mapContainerRef.current, {
                        center: defaultLocation,
                        zoom: 14,
                    });
                    setMap(newMap);

                    // Marcador en ubicación por defecto
                    userMarkerRef.current = new window.google.maps.Marker({
                        position: defaultLocation,
                        map: newMap,
                        title: "Ubicación por defecto (Carmen, Campeche)",
                        icon: {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 14,
                            fillColor: '#FFA500',
                            fillOpacity: 1,
                            strokeWeight: 4,
                            strokeColor: '#FFFFFF',
                        },
                        zIndex: 1000,
                    });

                    // Mostrar alerta de que se usó ubicación por defecto
                    Swal.fire({
                        icon: "info",
                        title: "Ubicación por defecto",
                        text: "No se pudo detectar tu ubicación. Se utilizó Carmen, Campeche como punto de partida.",
                        timer: 5000,
                    });
                },
                { 
                    enableHighAccuracy: true, 
                    timeout: 10000, 
                    maximumAge: 0 
                }
            );
        } else {
            console.error("El navegador no soporta la geolocalización.");
            const defaultLocation = { lat: 18.647336, lng: -91.820681 };
            setUserLocation(defaultLocation);
            const newMap = new window.google.maps.Map(mapContainerRef.current, {
                center: defaultLocation,
                zoom: 14,
            });
            setMap(newMap);

            // Marcador en ubicación por defecto
            userMarkerRef.current = new window.google.maps.Marker({
                position: defaultLocation,
                map: newMap,
                title: "Ubicación por defecto (Carmen, Campeche)",
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 14,
                    fillColor: '#FFA500',
                    fillOpacity: 1,
                    strokeWeight: 4,
                    strokeColor: '#FFFFFF',
                },
                zIndex: 1000,
            });
        }
    };

    // Función para limpiar marcadores
    const clearMarkers = () => {
        clientMarkersRef.current.forEach(marker => marker.setMap(null));
        clientMarkersRef.current = [];
        if (polylineRef.current) {
            if (polylineRef.current instanceof Array) {
                polylineRef.current.forEach(polyline => polyline.setMap(null));
            } else {
                polylineRef.current.setMap(null);
            }
            polylineRef.current = null;
        }
    };

    // Función para dibujar la ruta real con calles
    const drawRouteWithDirections = (directionService, visibleClients) => {
        if (!map || !optimizedRoute || optimizedRoute.length === 0 || !userLocation) return;

        if (viewMode === 'all') {
            // Modo "Ver Todo": dibujar ruta completa con batches
            const maxWaypoints = 8; // Google Maps Directions API permite máximo 8 waypoints
            
            const drawBatch = (startIdx) => {
                const endIdx = Math.min(startIdx + maxWaypoints, optimizedRoute.length);
                const batch = optimizedRoute.slice(startIdx, endIdx);

                const waypoints = [];
                // Agregar todos menos el último como waypoints
                for (let i = 0; i < batch.length - 1; i++) {
                    waypoints.push({
                        location: new window.google.maps.LatLng(batch[i].latitude, batch[i].longitude),
                        stopover: true
                    });
                }

                const request = {
                    origin: startIdx === 0 ? userLocation : new window.google.maps.LatLng(optimizedRoute[startIdx].latitude, optimizedRoute[startIdx].longitude),
                    destination: new window.google.maps.LatLng(batch[batch.length - 1].latitude, batch[batch.length - 1].longitude),
                    waypoints: waypoints,
                    travelMode: window.google.maps.TravelMode.DRIVING,
                    optimizeWaypoints: false // Mantener el orden de la ruta
                };

                directionService.route(request, (result, status) => {
                    if (status === window.google.maps.DirectionsStatus.OK) {
                        const route = result.routes[0];
                        const polyline = new window.google.maps.Polyline({
                            path: route.overview_path,
                            geodesic: true,
                            strokeColor: '#3B82F6',
                            strokeOpacity: 0.7,
                            strokeWeight: 3,
                            map: map,
                        });
                        
                        if (polylineRef.current instanceof Array) {
                            polylineRef.current.push(polyline);
                        } else {
                            if (polylineRef.current) polylineRef.current.setMap(null);
                            polylineRef.current = [polyline];
                        }

                        // Dibujar la siguiente batcha si es necesario
                        if (endIdx < optimizedRoute.length) {
                            drawBatch(endIdx);
                        }
                    } else {
                        console.error('Directions request failed: ' + status);
                        // Fallback a línea recta si falla
                        const routePath = [
                            startIdx === 0 ? userLocation : new window.google.maps.LatLng(optimizedRoute[startIdx].latitude, optimizedRoute[startIdx].longitude),
                            ...batch.map(c => ({ lat: c.latitude, lng: c.longitude }))
                        ];
                        const fallbackPolyline = new window.google.maps.Polyline({
                            path: routePath,
                            geodesic: true,
                            strokeColor: '#3B82F6',
                            strokeOpacity: 0.7,
                            strokeWeight: 3,
                            map: map,
                        });
                        
                        if (polylineRef.current instanceof Array) {
                            polylineRef.current.push(fallbackPolyline);
                        } else {
                            if (polylineRef.current) polylineRef.current.setMap(null);
                            polylineRef.current = [fallbackPolyline];
                        }

                        if (endIdx < optimizedRoute.length) {
                            drawBatch(endIdx);
                        }
                    }
                });
            };

            drawBatch(0);
        } else if (viewMode === 'one' || viewMode === 'three') {
            // Modo "Uno por Uno" o "3 en 3": dibujar ruta desde usuario a los clientes visibles
            if (!visibleClients || visibleClients.length === 0) return;

            const waypoints = [];
            // Agregar todos los clientes visibles menos el último como waypoints
            for (let i = 0; i < visibleClients.length - 1; i++) {
                waypoints.push({
                    location: new window.google.maps.LatLng(visibleClients[i].latitude, visibleClients[i].longitude),
                    stopover: true
                });
            }

            const request = {
                origin: userLocation,
                destination: new window.google.maps.LatLng(visibleClients[visibleClients.length - 1].latitude, visibleClients[visibleClients.length - 1].longitude),
                waypoints: waypoints,
                travelMode: window.google.maps.TravelMode.DRIVING,
                optimizeWaypoints: false // Mantener el orden de la ruta
            };

            directionService.route(request, (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                    const route = result.routes[0];
                    const polyline = new window.google.maps.Polyline({
                        path: route.overview_path,
                        geodesic: true,
                        strokeColor: '#3B82F6',
                        strokeOpacity: 0.7,
                        strokeWeight: 3,
                        map: map,
                    });
                    
                    polylineRef.current = polyline;
                } else {
                    console.error('Directions request failed: ' + status);
                    // Fallback a línea recta si falla
                    const routePath = [userLocation, ...visibleClients.map(c => ({ lat: c.latitude, lng: c.longitude }))];
                    const fallbackPolyline = new window.google.maps.Polyline({
                        path: routePath,
                        geodesic: true,
                        strokeColor: '#3B82F6',
                        strokeOpacity: 0.7,
                        strokeWeight: 3,
                        map: map,
                    });
                    
                    polylineRef.current = fallbackPolyline;
                }
            });
        }
    };

    // Función para dibujar marcadores y ruta en el mapa
    const drawMarkersAndRoute = () => {
        if (!map || !optimizedRoute || optimizedRoute.length === 0) return;

        clearMarkers();

        let visibleClients = [];
        if (viewMode === 'all') {
            visibleClients = optimizedRoute;
        } else if (viewMode === 'one') {
            if (currentIndex < optimizedRoute.length) {
                visibleClients = [optimizedRoute[currentIndex]];
            }
        } else if (viewMode === 'three') {
            visibleClients = optimizedRoute.slice(currentIndex, currentIndex + 3);
        }

        // Encontrar el primer cliente no visitado
        let firstUnvisitedIdx = -1;
        if (viewMode === 'all') {
            firstUnvisitedIdx = optimizedRoute.findIndex(c => !visitedClients.has(c.originalIndex));
        } else if (viewMode === 'one') {
            firstUnvisitedIdx = 0;
        } else if (viewMode === 'three') {
            firstUnvisitedIdx = visibleClients.findIndex(c => !visitedClients.has(c.originalIndex));
        }

        // Dibujar marcadores
        visibleClients.forEach((client, idx) => {
            const isVisited = visitedClients.has(client.originalIndex);
            const color = isVisited ? '#10B981' : '#EF4444';
            const scale = idx === 0 ? 16 : 14; // Marcadores más grandes
            const routeIndex = optimizedRoute.indexOf(client) + 1; // Número de posición en la ruta completa
            const isFirstUnvisited = viewMode === 'all' ? optimizedRoute.indexOf(client) === firstUnvisitedIdx : idx === firstUnvisitedIdx;

            const marker = new window.google.maps.Marker({
                position: { lat: client.latitude, lng: client.longitude },
                map: map,
                title: client.clientName,
                label: {
                    text: routeIndex.toString(),
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px'
                },
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: scale,
                    fillColor: color,
                    fillOpacity: isVisited ? 0.7 : 1,
                    strokeWeight: 4, // Borde más grueso
                    strokeColor: '#FFFFFF',
                },
                animation: isFirstUnvisited && !isVisited ? window.google.maps.Animation.BOUNCE : null,
            });

            const infoWindow = new window.google.maps.InfoWindow({
                content: `
                    <div style="padding: 12px; max-width: 250px; font-family: Arial, sans-serif;">
                        <h4 style="margin: 0 0 8px 0; color: #1f2937;">Parada #${routeIndex}</h4>
                        <p style="margin: 6px 0; font-size: 13px;"><strong>Cliente:</strong> ${client.clientName}</p>
                        <p style="margin: 6px 0; font-size: 12px;"><strong>Dirección:</strong> ${client.clientDireccion}</p>
                        <p style="margin: 6px 0; font-size: 12px; color: #3b82f6;"><strong>Distancia:</strong> ${client.distance.toFixed(2)} km</p>
                        <p style="margin: 8px 0 0 0; font-size: 11px; color: #6b7280;">${isVisited ? '✓ Visitado' : 'Pendiente'}</p>
                    </div>
                `,
            });

            marker.addListener('click', () => {
                infoWindow.open(map, marker);
            });

            clientMarkersRef.current.push(marker);
        });

        // Re-crear marcador de ubicación actual encima de todo
        if (userMarkerRef.current) {
            userMarkerRef.current.setMap(null);
        }
        userMarkerRef.current = new window.google.maps.Marker({
            position: userLocation,
            map: map,
            title: "Mi ubicación actual",
            icon: {
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 14,
                fillColor: '#4285F4',
                fillOpacity: 1,
                strokeWeight: 4,
                strokeColor: '#FFFFFF',
            },
            zIndex: 1000,
        });

        // Dibujar ruta con Directions Service
        if (userLocation && window.google) {
            const directionService = new window.google.maps.DirectionsService();
            drawRouteWithDirections(directionService, visibleClients);
        }

        // Centrar el mapa
        if (visibleClients.length > 0 && userLocation) {
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend(userLocation);
            visibleClients.forEach(client => {
                bounds.extend({ lat: client.latitude, lng: client.longitude });
            });
            map.fitBounds(bounds, 50);
        }
    };


    const handleSearchAdresses = () => {
        if (!asesorSelect) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Debes seleccionar un asesor.",
            });
            return;
        }
        if (!fecha) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Debes seleccionar una fecha.",
            });
            return;
        }
        if (fecha !== "" && fecha !== null && asesorSelect !== null) {
            setLoadingPrint(true);

            api(SERVICE.reportPagosMaps + `?date=${fecha}&advisorId=${asesorSelect.value}`)
                .then((data) => {
                    if (data.data.data) {
                        const info = data.data.data.data;
                        if (info && info.length > 0) {
                            setClients(info);
                            setVisitedClients(new Set());
                            setCurrentIndex(0);
                            
                            // Calcular la ruta optimizada
                            if (userLocation) {
                                const route = optimizeRoute(userLocation, info);
                                setOptimizedRoute(route);
                            }
                        } else {
                            setClients([]);
                            setOptimizedRoute([]);
                            Swal.fire({
                                icon: "info",
                                title: "Sin clientes",
                                text: "No se encontraron clientes para la fecha y asesor seleccionados.",
                            });
                        }
                    }
                })
                .catch((err) => {
                    console.log(err);
                    Swal.fire({
                        position: "center",
                        icon: "error",
                        title: "Error al obtener la información",
                        text: "Inténtalo de nuevo",
                        showConfirmButton: true
                    });
                })
                .finally(() => {
                    setLoadingPrint(false);
                })
        }
    }

    useEffect(() => {
        handleGetAsesortes();
    }, []);

    // Dibujar marcadores cuando cambia la ruta optimizada o el modo de visualización
    useEffect(() => {
        if (map && optimizedRoute.length > 0) {
            drawMarkersAndRoute();
        }
    }, [map, optimizedRoute, viewMode, currentIndex, visitedClients]);

    // Recalcular ruta cuando cambia la ubicación del usuario
    useEffect(() => {
        if (userLocation && clients && clients.length > 0) {
            const route = optimizeRoute(userLocation, clients);
            setOptimizedRoute(route);
        }
    }, [userLocation]);

    const handleMarkVisited = (index) => {
        if (viewMode === 'all') {
            const newVisited = new Set(visitedClients);
            newVisited.add(optimizedRoute[index].originalIndex);
            setVisitedClients(newVisited);
        } else if (viewMode === 'one') {
            const newVisited = new Set(visitedClients);
            newVisited.add(optimizedRoute[currentIndex].originalIndex);
            setVisitedClients(newVisited);
        } else if (viewMode === 'three') {
            const newVisited = new Set(visitedClients);
            const clientToMark = optimizedRoute[currentIndex + index];
            if (clientToMark) {
                newVisited.add(clientToMark.originalIndex);
                setVisitedClients(newVisited);
            }
        }
    };

    const handleNextClient = () => {
        if (viewMode === 'one') {
            if (currentIndex < optimizedRoute.length - 1) {
                setCurrentIndex(currentIndex + 1);
            }
        } else if (viewMode === 'three') {
            if (currentIndex + 3 < optimizedRoute.length) {
                setCurrentIndex(currentIndex + 3);
            }
        }
    };

    const handlePrevClient = () => {
        if (viewMode === 'one') {
            if (currentIndex > 0) {
                setCurrentIndex(currentIndex - 1);
            }
        } else if (viewMode === 'three') {
            if (currentIndex > 0) {
                setCurrentIndex(Math.max(0, currentIndex - 3));
            }
        }
    };

    const getVisibleClients = () => {
        if (viewMode === 'all') {
            return optimizedRoute;
        } else if (viewMode === 'one') {
            return optimizedRoute.slice(currentIndex, currentIndex + 1);
        } else if (viewMode === 'three') {
            return optimizedRoute.slice(currentIndex, currentIndex + 3);
        }
        return [];
    };

    const getRemainingCount = () => {
        if (viewMode === 'all') {
            return optimizedRoute.length - visitedClients.size;
        } else if (viewMode === 'one') {
            return optimizedRoute.length - visitedClients.size;
        } else if (viewMode === 'three') {
            const visibleClients = getVisibleClients();
            return visibleClients.filter(c => !visitedClients.has(c.originalIndex)).length;
        }
        return 0;
    };

    const handleShowURLsPreview = () => {
        if (!optimizedRoute || optimizedRoute.length === 0) {
            Swal.fire({
                icon: "warning",
                title: "Sin datos",
                text: "No hay clientes para exportar",
            });
            return;
        }

        const urlsData = generateGoogleMapsURLs(userLocation, optimizedRoute);
        setUrlsPreview(urlsData);
        setShowURLPreview(true);
    };
    return (
        <>
            <style jsx>{`
        .map-container {
          height: 70vh;
          width: 100%;
          border-radius: 8px;
          min-height: 300px;
        }
        .sidebar {
          height: 70vh;
          overflow-y: auto;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        @media (max-width: 768px) {
          .map-container {
            height: 50vh;
            min-height: 250px;
          }
          .sidebar {
            height: auto;
            max-height: 60vh;
            padding: 15px;
          }
        }
        @media (max-width: 480px) {
          .map-container {
            height: 40vh;
            min-height: 200px;
          }
          .sidebar {
            height: auto;
            max-height: 50vh;
            padding: 12px;
          }
        }
        .client-item {
          background: white;
          padding: 15px;
          margin-bottom: 10px;
          border-radius: 6px;
          border-left: 4px solid #3B82F6;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .client-item:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transform: translateX(2px);
        }
        @media (max-width: 768px) {
          .client-item {
            padding: 12px;
            margin-bottom: 8px;
          }
        }
        @media (max-width: 480px) {
          .client-item {
            padding: 10px;
            margin-bottom: 6px;
          }
        }
        .client-item.visited {
          border-left-color: #10B981;
          opacity: 0.6;
          background: #f0fdf4;
        }
        .client-item.current {
          background: #dbeafe;
          border-left-color: #ef4444;
        }
        .view-mode-buttons {
          display: flex;
          gap: 8px;
          margin-bottom: 15px;
          flex-wrap: wrap;
        }
        .view-mode-btn {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          background: white;
          cursor: pointer;
          border-radius: 6px;
          font-size: 12px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 80px;
        }
        @media (max-width: 768px) {
          .view-mode-btn {
            padding: 6px 10px;
            font-size: 11px;
            min-width: 70px;
          }
        }
        @media (max-width: 480px) {
          .view-mode-btn {
            padding: 6px 8px;
            font-size: 10px;
            min-width: 60px;
          }
        }
        .view-mode-btn.active {
          background: #3B82F6;
          color: white;
          border-color: #3B82F6;
        }
        .route-stats {
          background: white;
          padding: 15px;
          border-radius: 6px;
          margin-bottom: 15px;
          border-left: 4px solid #8B5CF6;
        }
        .stats-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
          font-size: 14px;
        }
        @media (max-width: 768px) {
          .route-stats {
            padding: 12px;
            margin-bottom: 12px;
          }
          .stats-item {
            font-size: 13px;
            margin-bottom: 6px;
          }
        }
        @media (max-width: 480px) {
          .route-stats {
            padding: 10px;
            margin-bottom: 10px;
          }
          .stats-item {
            font-size: 12px;
            margin-bottom: 5px;
          }
        }
        .confirm-btn {
          width: 100%;
          padding: 10px;
          background: #10B981;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: bold;
          margin-top: 10px;
          transition: all 0.3s ease;
        }
        .confirm-btn:hover {
          background: #059669;
        }
        .confirm-btn:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }
        @keyframes dropdownSlideIn {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
            <WindowLayout
                title="RUTAS"
                route={["Inicio", "Rutas"]}
                actions={[() => setMenuActual("homeMenu")]}
                icon={<FaMapMarkedAlt />}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "left",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "10px",
                        marginBottom: "15px",
                    }}
                >
                    <div className={styles.itemForm} style={{ zIndex: 7 }}>
                        <label className="form-label">Asesor: </label>
                        <Select
                            isDisabled={asesorLocked}
                            isClearable
                            options={asesores}
                            className=""
                            placeholder="Asesor"
                            value={asesorSelect}
                            onChange={(e) => setAsesorSelect(e)}
                        />
                    </div>

                    <div className={styles.itemForm}>
                        <label className="form-label">Fecha:</label>
                        <input
                            type="date"
                            className="form-control"
                            placeholder="Fecha"
                            value={fecha}
                            onChange={(e) => setFecha(e.target.value)}
                        />
                    </div>
                    <div className={styles.itemForm}>
                        <button style={{ marginTop: '30px' }} className="btn btn-primary" onClick={handleSearchAdresses} disabled={loadingPrint || asesorSelect == null}>
                            {loadingPrint ? <div className="spinner-border text-light spinner-border-sm" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div> : <>
                                Consultar Rutas
                            </>}
                        </button>
                    </div>
                </div>
                <div style={{ 
                  display: "flex", 
                  flexDirection: "row", 
                  marginTop: "20px", 
                  gap: "20px",
                  width: "100%",
                }}>
                    <style jsx>{`
                        @media (max-width: 768px) {
                            .routes-container {
                                flex-direction: column !important;
                                gap: 15px !important;
                            }
                        }
                        @media (max-width: 480px) {
                            .routes-container {
                                flex-direction: column !important;
                                gap: 12px !important;
                            }
                        }
                    `}</style>
                    <div className="routes-container" style={{ 
                      display: "flex", 
                      flexDirection: "row", 
                      gap: "20px",
                      width: "100%",
                    }}>
                    <div ref={mapContainerRef} className="map-container" style={{ flex: "3", minWidth: 0 }}>
                        {/* El mapa se renderizará aquí */}
                    </div>
                    <div className="sidebar" style={{ 
                      flex: "1", 
                      minWidth: 0,
                      minHeight: "300px",
                    }}>
                        <h3>Detalle de la Ruta</h3>
                        
                        <div className="route-stats">
                            <div className="stats-item">
                                <span>Total de clientes:</span>
                                <strong>{optimizedRoute.length}</strong>
                            </div>
                            <div className="stats-item">
                                <span>Visitados:</span>
                                <strong style={{ color: '#10B981' }}>{visitedClients.size}</strong>
                            </div>
                            <div className="stats-item">
                                <span>Pendientes:</span>
                                <strong style={{ color: '#EF4444' }}>{getRemainingCount()}</strong>
                            </div>
                        </div>

                        {optimizedRoute.length > 0 && (
                            <button
                                className="btn btn-success"
                                onClick={handleShowURLsPreview}
                                style={{ width: '100%', marginBottom: '15px' }}
                            >
                                <FaDownload /> Exportar/Previsualizar
                            </button>
                        )}

                        <div className="view-mode-buttons">
                            <button
                                className={`view-mode-btn ${viewMode === 'all' ? 'active' : ''}`}
                                onClick={() => { setViewMode('all'); setCurrentIndex(0); }}
                            >
                                Ver Todo
                            </button>
                            <button
                                className={`view-mode-btn ${viewMode === 'one' ? 'active' : ''}`}
                                onClick={() => { setViewMode('one'); setCurrentIndex(0); }}
                            >
                                Uno por Uno
                            </button>
                            <button
                                className={`view-mode-btn ${viewMode === 'three' ? 'active' : ''}`}
                                onClick={() => { setViewMode('three'); setCurrentIndex(0); }}
                            >
                                3 en 3
                            </button>
                        </div>

                        {optimizedRoute.length > 0 ? (
                            <>
                                {getVisibleClients().map((client, idx) => {
                                    const isVisited = visitedClients.has(client.originalIndex);
                                    const routeIndex = optimizedRoute.indexOf(client);
                                    return (
                                        <div
                                            key={client.originalIndex}
                                            className={`client-item ${isVisited ? 'visited' : ''} ${viewMode === 'one' && idx === 0 ? 'current' : ''}`}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <strong style={{ fontSize: '14px', display: 'block', marginBottom: '5px' }}>
                                                        #{routeIndex + 1} - {client.clientName}
                                                    </strong>
                                                    <p style={{ fontSize: '12px', margin: '5px 0', color: '#666' }}>
                                                        {client.clientDireccion}
                                                    </p>
                                                    <p style={{ fontSize: '11px', margin: '5px 0', color: '#999' }}>
                                                        Distancia: {client.distance.toFixed(2)} km
                                                    </p>
                                                </div>
                                                {isVisited ? (
                                                    <FaCheckCircle style={{ color: '#10B981', marginLeft: '10px', marginTop: '5px' }} />
                                                ) : (
                                                    <FaTimesCircle style={{ color: '#EF4444', marginLeft: '10px', marginTop: '5px' }} />
                                                )}
                                            </div>
                                            {!isVisited && (
                                                <button
                                                    className="confirm-btn"
                                                    onClick={() => handleMarkVisited(idx)}
                                                >
                                                    Confirmar Visita
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}

                                {(viewMode === 'one' || viewMode === 'three') && (
                                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={handlePrevClient}
                                            disabled={currentIndex === 0}
                                            style={{ flex: 1 }}
                                        >
                                            <FaChevronLeft /> Anterior
                                        </button>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={handleNextClient}
                                            disabled={
                                                viewMode === 'one' ? currentIndex >= optimizedRoute.length - 1 :
                                                    currentIndex + 3 >= optimizedRoute.length
                                            }
                                            style={{ flex: 1 }}
                                        >
                                            Siguiente <FaChevronRight />
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p style={{ textAlign: 'center', color: '#999', marginTop: '20px' }}>
                                Selecciona un asesor y una fecha para ver la ruta
                            </p>
                        )}
                    </div>
                    </div>
                </div>

                {/* Modal de Previsualización de URLs */}
                {showURLPreview && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2000,
                        padding: '10px',
                    }}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            maxWidth: '700px',
                            width: '100%',
                            maxHeight: '85vh',
                            overflowY: 'auto',
                            padding: '20px',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                        }}>
                            <h2 style={{ marginTop: 0, marginBottom: '15px', color: '#1f2937', fontSize: 'clamp(18px, 5vw, 24px)' }}>
                                Previsualización de Rutas
                            </h2>
                            <p style={{ color: '#6b7280', marginBottom: '15px', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                                {urlsPreview.length} ruta(s) disponible(s) para {optimizedRoute.length} cliente(s)
                            </p>

                            {urlsPreview.map((urlObj, idx) => (
                                <div key={idx} style={{
                                    backgroundColor: '#f3f4f6',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    marginBottom: '12px',
                                    border: '1px solid #e5e7eb',
                                }}>
                                    <div style={{ marginBottom: '10px' }}>
                                        <h4 style={{ margin: '0 0 6px 0', color: '#1f2937', fontSize: 'clamp(14px, 4vw, 16px)' }}>
                                            Ruta {idx + 1} de {urlsPreview.length}
                                        </h4>
                                        <p style={{ margin: '0', fontSize: 'clamp(11px, 3vw, 13px)', color: '#6b7280', wordBreak: 'break-word' }}>
                                            <strong>{urlObj.clients.length} cliente(s):</strong> {urlObj.clients.map((c, i) => (
                                                <span key={i}>
                                                    {c.clientName}
                                                    {i < urlObj.clients.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                        </p>
                                        <p style={{ margin: '4px 0 0 0', fontSize: 'clamp(10px, 2vw, 12px)', color: '#999' }}>
                                            De: {optimizedRoute.indexOf(urlObj.clients[0]) + 1} a {optimizedRoute.indexOf(urlObj.clients[urlObj.clients.length - 1]) + 1}
                                        </p>
                                    </div>

                                    <div style={{
                                        backgroundColor: 'white',
                                        padding: '8px',
                                        borderRadius: '6px',
                                        marginBottom: '10px',
                                        border: '1px solid #d1d5db',
                                        wordBreak: 'break-all',
                                        fontSize: 'clamp(9px, 2vw, 11px)',
                                        color: '#3b82f6',
                                        maxHeight: '60px',
                                        overflowY: 'auto',
                                    }}>
                                        {urlObj.url}
                                    </div>

                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(urlObj.url);
                                                Swal.fire({
                                                    icon: 'success',
                                                    title: 'Copiado',
                                                    text: 'URL copiada al portapapeles',
                                                    timer: 2000,
                                                    showConfirmButton: false,
                                                });
                                            }}
                                            style={{
                                                flex: '1 1 auto',
                                                minWidth: '80px',
                                                padding: '8px 10px',
                                                backgroundColor: '#6366f1',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: 'clamp(11px, 2vw, 12px)',
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                            }}
                                        >
                                            <FaCopy /> Copiar
                                        </button>
                                        <button
                                            onClick={() => window.open(urlObj.url, '_blank')}
                                            style={{
                                                flex: '1 1 auto',
                                                minWidth: '80px',
                                                padding: '8px 10px',
                                                backgroundColor: '#3b82f6',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: 'clamp(11px, 2vw, 12px)',
                                                fontWeight: 'bold',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                            }}
                                        >
                                            <FaExternalLinkAlt /> Abrir
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <div style={{ display: 'flex', gap: '8px', marginTop: '15px', flexWrap: 'wrap' }}>
                                <button
                                    onClick={() => {
                                        exportToExcel(optimizedRoute, userLocation);
                                        setShowURLPreview(false);
                                    }}
                                    style={{
                                        flex: '1 1 auto',
                                        minWidth: '120px',
                                        padding: '10px',
                                        backgroundColor: '#10B981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: 'clamp(12px, 3vw, 14px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    <FaDownload /> Exportar a Excel
                                </button>
                                <button
                                    onClick={() => setShowURLPreview(false)}
                                    style={{
                                        flex: '1 1 auto',
                                        minWidth: '100px',
                                        padding: '10px',
                                        backgroundColor: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        fontSize: 'clamp(12px, 3vw, 14px)',
                                    }}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </WindowLayout>
        </>
    );
}


